/**
 * BMA360D amplifier escalation matrix (PIG-297 / WDT-78).
 *
 * The BMA360D has an on-board amplifier (up to 4 speakers across 4 channels at
 * 15W per channel) and offloads to external Tesira AMP-450BP units (up to 8
 * speakers each) as the speaker count grows. Total on-board capacity scales
 * with tile count (`ONBOARD_AMP_CAPACITY * unitCount`) — each BMA360D tile has
 * its own on-board amp.
 *
 * The on-board amp is used only when doing so actually eliminates the need for
 * at least one external AMP-450BP unit — i.e. when driving the same speakers
 * without it would require strictly more AMP-450BP units than driving them
 * with it. If the AMP-450BP count comes out the same either way, using the
 * on-board amp would only add a PoE injector with zero savings, so it's left
 * idle. This is evaluated against the room's *total* on-board capacity, not a
 * per-block remainder — so it correctly credits multi-tile rooms even when the
 * total speaker count is an exact multiple of the AMP-450BP capacity (8): e.g.
 * 2 tiles (8-speaker on-board capacity) + 8 speakers needs zero external amps,
 * not one.
 *
 * When the on-board amp drives speakers the design requires a PoE injector
 * (910-3200-209) to power it; when AMP-450BP units alone cover all speakers, no
 * injector is needed.
 *
 * Reference matrix for a single tile (4-speaker on-board capacity):
 *   ≤4    → on-board only             (PoE: yes)
 *   5–8   → 1× AMP-450BP              (PoE: no)
 *   9–12  → on-board + 1× AMP-450BP   (PoE: yes)
 *   13–16 → 2× AMP-450BP              (PoE: no)
 *   17–20 → on-board + 2× AMP-450BP   (PoE: yes)
 *   … the pattern continues by the same rule beyond 20, and shifts up for
 *   additional tiles (larger on-board capacity absorbs more before an extra
 *   AMP-450BP becomes unavoidable).
 *
 * Mirrors the existing TCM-X amp-selection paradigm in logic.js
 * (`calculateEquipmentTable`): minimise external amps, prefer the integrated
 * amp only when it is the cheaper way to cover the leftover speakers.
 */

/** On-board amplifier capacity: 4 speakers across 4 channels @ 15W/channel. */
export const ONBOARD_AMP_CAPACITY = 4;
/** External Tesira AMP-450BP capacity: up to 8 speakers per unit. */
export const AMP_450BP_CAPACITY = 8;
/**
 * Wall-speaker bridge-mode cap (PIG-479 / WDT-78): the on-board amp drives at
 * most 2 wall speakers in bridge mode (2×30W into a 4Ω load).
 */
export const BRIDGE_MODE_MAX_SPEAKERS = 2;

export interface Bma360dAmpConfig {
  /** True when the BMA360D's integrated amplifier drives the remainder speakers. */
  onboardAmpUsed: boolean;
  /** Number of external Tesira AMP-450BP units required. */
  amp450bpQty: number;
  /** True when a PoE injector (910-3200-209) is required to power the on-board amp. */
  poeInjectorNeeded: boolean;
}

/**
 * Compute the BMA360D amplifier configuration for a given total speaker count.
 *
 * Pure function — no room/area dependency. The caller is responsible for passing
 * the total ceiling/pendant speaker count across every BMA360D unit in the room.
 * Wall-speaker (bridge-mode) configurations are a separate special case handled
 * by the caller and do not flow through this matrix (see PIG-479 / WDT-78).
 *
 * `unitCount` is the number of BMA360D tiles placed in the room (default 1, which
 * reproduces the original single-tile escalation matrix exactly). Each tile has its
 * own on-board amp, so total on-board capacity scales with tile count
 * (`ONBOARD_AMP_CAPACITY * unitCount`) — a 2-tile room can absorb an 8-speaker
 * remainder on-board, not just 4.
 *
 * Bug fix history:
 *  - on-board capacity previously used a flat 4 regardless of `unitCount`, which
 *    under-counted capacity for multi-unit rooms and added unnecessary AMP-450BP
 *    units (e.g. 2 tiles + 6 speakers wrongly required an external amp even
 *    though both tiles' on-board amps together cover all 6).
 *  - after scaling capacity by `unitCount`, a second issue remained: the
 *    remainder was computed against `AMP_450BP_CAPACITY` blocks (8) *before*
 *    on-board capacity was considered, so an exact multiple of 8 (e.g. 2 tiles +
 *    8 speakers) always bought a full AMP-450BP even when on-board capacity
 *    alone could cover it for free. Fixed by comparing total AMP-450BP count
 *    with vs. without on-board use, rather than reasoning about a single
 *    leftover remainder.
 */
export const getBma360dAmpConfig = (spkrCount: number, unitCount = 1): Bma360dAmpConfig => {
  const count = Number.isFinite(spkrCount) ? Math.max(0, Math.floor(spkrCount)) : 0;
  const units = Number.isFinite(unitCount) ? Math.max(1, Math.floor(unitCount)) : 1;
  const onboardCapacity = ONBOARD_AMP_CAPACITY * units;

  if (count === 0) {
    return { onboardAmpUsed: false, amp450bpQty: 0, poeInjectorNeeded: false };
  }

  // How many AMP-450BP units would be needed with vs. without using the
  // on-board amp(s) at all. Comparing full amp counts (rather than a single
  // remainder) correctly credits on-board capacity even when it spans, or
  // exactly matches, one or more whole AMP-450BP-sized blocks.
  const ampsWithoutOnboard = Math.ceil(count / AMP_450BP_CAPACITY);
  const overflow = Math.max(0, count - onboardCapacity);
  const ampsWithOnboard = Math.ceil(overflow / AMP_450BP_CAPACITY);

  // Only use the on-board amp(s) when doing so actually eliminates the need for
  // at least one external AMP-450BP; otherwise it's a pure extra cost (PoE
  // injector) for zero savings, since the same AMP-450BP count is needed either way.
  if (ampsWithOnboard < ampsWithoutOnboard) {
    return { onboardAmpUsed: true, amp450bpQty: ampsWithOnboard, poeInjectorNeeded: true };
  }
  return { onboardAmpUsed: false, amp450bpQty: ampsWithoutOnboard, poeInjectorNeeded: false };
};

/**
 * Wall-speaker special case (WDT-78, current approach).
 *
 * When a room uses wall speakers (≤2 per room) the escalation matrix does NOT
 * apply. Per WDT-78 the wall speakers are always driven by an external
 * AMP-450BP — the BMA360D on-board amp is explicitly NOT used for wall-speaker
 * output (no daisy chaining: each speaker connects to its own AMP-450BP channel
 * via a CCA, 1× per speaker — emitted separately per WDT-138). A single AMP-450BP
 * (4 channels) covers the ≤2 speakers, so `amp450bpQty` is 1.
 *
 * Because the on-board amp is idle, the BMA360D is powered via PoE+ from the Forte
 * DSP, so NO EasyConnect POE-90W injector is required (`poeInjectorNeeded: false`).
 *
 * Coexistence of wall speakers with ceiling/pendant speakers in the same room is
 * not supported in MVP — and is not representable either, since an area carries a
 * single speaker mounting type. The caller selects this config solely on the
 * speaker mounting type; speaker quantity is capped at `BRIDGE_MODE_MAX_SPEAKERS`
 * by the speaker-placement logic, independent of this amp calculation.
 */
export const getBma360dWallSpeakerAmpConfig = (): Bma360dAmpConfig => ({
  onboardAmpUsed: false,
  amp450bpQty: 1, // one 4-channel AMP-450BP drives the ≤2 wall speakers
  poeInjectorNeeded: false,
});
